/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/naming-convention */
import { PropsWithChildren, ReactElement, ReactNode } from 'react';

import { QueryParamValue } from 'app/api/apiClient/generated/baseClient';
import { AddressDto } from 'app/api/apiClient/generated/types';
import i18n from 'app/services/i18n';

import { formatParamDate } from './dates';
import { getLanguage } from './i18n';

// Local Typings
export interface ParsedToken {
  Api: string;
  CGuid: string;
  CId: string;
  CSRF: string;
  FName: string;
  Roles: string;
  TId: string;
  UGuid: string;
  UId: string;
  exp: number;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': string;
  iss: string;
}

interface DateOption {
  dateTypeId?: string;
  endDate: Date;
  startDate: Date;
}

interface DecodeURIOptions {
  defaultReturnValue?: string;
}

// Local Variables
const convertBooleans = (str: string) => {
  const typeIsBool = str === 'true' || str === 'false';

  if (typeIsBool) {
    return str === 'true';
  }

  return str;
};

// Exports
export const capitalize = (str: string) => {
  if (str.trim().length === 0) {
    return str;
  }
  const downcase = str.trim().toLowerCase();
  const firstChar = downcase[0]?.toUpperCase() ?? '';

  return `${firstChar}${downcase.slice(1)}`;
};

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result
    ? {
      b: parseInt(result[3] ?? '',
        16),
      g: parseInt(result[2] ?? '',
        16),
      r: parseInt(result[1] ?? '',
        16),
    }
    : { b: 255, g: 255, r: 255 };
}

export const getDecodedURI = (value: string, options: DecodeURIOptions = {}): string => {
  const { defaultReturnValue = '' } = options;

  const splitChar = '%';

  let attemptCounter = 0;

  function attemptToDecode(val: string): string {
    if (!val || attemptCounter > value.length) {
      return defaultReturnValue;
    }

    attemptCounter += 1;

    try {
      return decodeURIComponent(val);
    } catch (_error) {
      const splitValue = val.split(splitChar);
      const { length } = splitValue;

      if (length > 1) {
        const joinedString = splitValue.slice(0,
          length - 1).join(splitChar);

        return attemptToDecode(joinedString);
      }

      return defaultReturnValue;
    }
  }

  return attemptToDecode(value);
};

export function isUrlImage(url?: string): boolean {
  if (!url) {
    return false;
  }

  const decoded = getDecodedURI(url.toLowerCase());

  const imageTypes = [
    '.svg',
    '.jpg',
    '.jpeg',
    '.png',
    'image/svg',
    'image/jpg',
    'image/jpeg',
    'image/png',
  ];

  return imageTypes.some(type => decoded.includes(type));
}

export const getCurrencySymbol = (currencyTypeName: string) =>
  (0).toLocaleString(undefined,
    {
      currency: currencyTypeName,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'currency',
    }).replace(/\d/g, '').trim();

export function getNumberFromPrice(price: string): number {
  const locale = getLanguage();

  const thousandSeparator = Intl.NumberFormat(locale).format(11111).replace(/\p{Number}/gu, '');
  const decimalSeparator = Intl.NumberFormat(locale).format(1.1).replace(/\p{Number}/gu, '');

  return parseFloat(price
    // Remove thousand separators
    .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
    // Replace decimal separator with dot
    .replace(new RegExp('\\' + decimalSeparator), '.')
    // Remove all non-numeric characters except dot
    .replace(/[^0-9.-]+/g, ''));
}

export function lightenHexColor(color: string, opacity: number) {
  const {
    b,
    g,
    r,
  } = hexToRgb(color);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * @deprecated pluralization varies across languages and i18n should be used instead
 */
export const pluralize = (
  count: number, singular: string, plural?: string, wordOnly?: boolean,
  // eslint-disable-next-line max-params
) => {
  const pluralWord = plural ?? `${singular}s`;
  const word = count === 1 ? singular : pluralWord;

  return wordOnly ? word : `${count} ${word}`;
};

const decodeAndConvertBooleans = (shouldConvertBooleans: boolean) => (value: string) => {
  const decodedValue = getDecodedURI(value);

  return shouldConvertBooleans ? convertBooleans(decodedValue) : decodedValue;
};

// used to parse the query string of a url
export function parseSearch(str: string, shouldConvertBooleans = true): Record<string, any> {
  const searchParams = new URLSearchParams(str);

  const results: Record<string, any> = {};
  for (const key of searchParams.keys()) {
    const value = searchParams.getAll(key);
    if (value.length > 1) {
      results[key] = value.map(decodeAndConvertBooleans(shouldConvertBooleans));
    } else {
      results[key] = decodeAndConvertBooleans(shouldConvertBooleans)(value[0] ?? '');
    }
  }

  return results;
}

export function replaceSubdomain(url: string, toSubdomain: string) {
  let localUrl = url;
  const prependedSubdomain = `://${toSubdomain}.`;
  // Prepend http://
  if (!/^\w*:\/\//.test(url)) {
    localUrl = `http://${url}`;
  }
  // Check if we got a subdomain in url
  const localSubdomain = localUrl.match(/\.\w*\b/g);
  if (!!localSubdomain && localSubdomain.length > 1) {
    return localUrl.replace(/(:\/\/\w+\.)/,
      prependedSubdomain);
  }

  return localUrl.replace(/:\/\/(\w*\.)/,
    prependedSubdomain);
}

export const stringifyQueryParams = (queryParams: Record<string, null | QueryParamValue | undefined>) => {
  const searchParams = new URLSearchParams();

  for (const key in queryParams) {
    const value = queryParams[key];
    if (value == null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach(v => {
        searchParams.append(key, encodeURIComponent(v));
      });
    } else {
      searchParams.append(key, encodeURIComponent(value));
    }
  }

  return searchParams.toString();
};

export function decodeJWT(base64Url: string): ParsedToken {
  const base64 = base64Url.replace(/-/g,
    '+').replace(/_/g,
    '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
    const charCode = `00${c.charCodeAt(0).toString(16)}`;

    return `%${(charCode).slice(-2)}`;
  }).join(''));

  return JSON.parse(jsonPayload) as ParsedToken;
}

export function parseJWT(token: string): ParsedToken {
  const base64Url = token.split('.')[1];

  return decodeJWT(base64Url ?? '');
}

export function addDateParams(defaultQueryString: string,
  option: DateOption): string {
  const params = stringifyQueryParams({
    ...parseSearch(defaultQueryString),
    endDate: formatParamDate(option.endDate),
    startDate: formatParamDate(option.startDate),
    ...option.dateTypeId && { dateTypeId: option.dateTypeId },
  });

  return `?${params}`;
}

export function formatPhoneNumber(phoneNumber: string): string {
  const numberMatch = phoneNumber.match(/\d+/g);
  const numbers = numberMatch ? numberMatch.map(Number).join('') : '';

  // [Austin]: Based on https://countrycode.org/, it appears that a country code will not be more than 6 digits
  if (numbers.length < 10 || numbers.length > 16) {
    return phoneNumber;
  }

  const lastFourCursor = numbers.length - 4;
  const midThreeCursor = numbers.length - 7;
  const areaCodeCursor = numbers.length - 10;

  const lastFour = numbers.slice(lastFourCursor);
  const midThree = numbers.slice(midThreeCursor,
    lastFourCursor);
  const areaCode = numbers.slice(areaCodeCursor,
    midThreeCursor);
  const countryCode = numbers.slice(0,
    areaCodeCursor) || '1';
  const countryCodeCursor = countryCode.length - 3;
  const formattedCountryCode = countryCode.length > 3
    ? `${countryCode.slice(0,
      countryCodeCursor)}-${countryCode.slice(countryCodeCursor)}`
    : countryCode;

  return `+${formattedCountryCode} (${areaCode}) ${midThree}-${lastFour}`;
}

export function formatCityStatePostal({
  city,
  postalCode,
  state,
}: Pick<AddressDto, 'city' | 'postalCode' | 'state'>) {
  const cityState = [city, state].filter(Boolean).join(', ');

  return postalCode ? `${cityState} ${postalCode}`.trim() : cityState;
}

export function copyToClipboard(value: string) {
  void window.navigator.clipboard.writeText(value);
}

export function joinStringsWithConjunction(items: string[]): string {
  const conjunction = i18n.t('util:string.conjunction', 'and');

  if (items.length <= 1) {
    return items.join('');
  }

  if (items.length === 2) {
    return items.join(` ${conjunction} `);
  }

  if (getLanguage() === 'en-US') {
    const lastIndex = items.length - 1;
    items[lastIndex] = `${conjunction} ${items[lastIndex]}`;

    return items.join(', ');
  }

  // For other languages, we don't use the Oxford comma
  return `${items.slice(0, -1).join(', ')} ${conjunction} ${items[items.length - 1]}`;
}

export function stripEmailDomain(email: string): string {
  return email.split('@')?.[0] ?? '';
}

export function removeLeadingZeros(initialString: string) {
  if (Number(initialString)) {
    return Number(initialString).toString();
  }

  return initialString;
}

export const isValidRedirectSubdomain = (subdomain: string) =>
  /^[A-Za-z0-9\-.]*$/.test(subdomain);

export const maybeParseNumber = (initialValue: string) => {
  if (initialValue === '') {
    return initialValue;
  }

  const numericValue = Number(initialValue);

  if (Number.isNaN(numericValue)) {
    return initialValue;
  }

  return numericValue;
};

export const getReactNodeText = (reactNode: ReactNode): string => {
  if (reactNode == null) {
    return '';
  }
  if (typeof reactNode === 'string') {
    return reactNode;
  }
  if (typeof reactNode === 'number') {
    return reactNode.toString();
  }
  if (reactNode instanceof Array) {
    return reactNode.map(getReactNodeText).join('');
  }
  if (typeof reactNode === 'object' && 'props' in reactNode) {
    return getReactNodeText((reactNode as ReactElement<PropsWithChildren>).props.children);
  }

  return '';
};

/**
 * only allows numbers and /
 * returns string in MM/DD/YYYY format
 * @deprecated replace with DateInput component
 */
export const parseDateInput = (value: string) => {
  // Prevent any input other than digits and "/"
  // Automatically add "/"
  return value
    .replace(/^(\d\d)(\d)$/g,
      '$1/$2')
    .replace(/^(\d\d\/\d\d)(\d+)$/g,
      '$1/$2')
    .replace(/[^\d/]/g,
      '');
};

export function trim(str: string): string {
  return str.trim();
}

export function toUpperCase(str: string): string {
  return str.toUpperCase();
}

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) {
    return str;
  }

  return str.substring(0, maxLength) + '...';
}
